/** @prettier */

import {Grid, Box, Container} from '@mui/material';
import {useState, useEffect} from 'react';
import {
  useLocation,
  useParams,
  Outlet,
  useOutletContext,
  LoaderFunctionArgs,
  useLoaderData,
} from 'react-router-dom';

//MTH component import
import UnitSettingTreeView from '../../component/UnitSettingTreeView';
import RegistrationBreadcrumbs from '../../component/RegistrationBreadcrumbs';
//API import
import {getCompositionsSitesBySiteID} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

type OutletContextType = {
  siteInfo: apiIF.res.getCompositionsSitesBySiteID | null;
  allGWInfo: apiIF.res.getCompositionsGateway | null;
  setAllGWInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGateway | null>
  >;
};

type loaderReturn = apiIF.res.getCompositionsSitesBySiteID;
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const siteInfo = await getCompositionsSitesBySiteID(params.siteID);

    if (siteInfo['site-authority'] !== 'admin') {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return siteInfo;
  } catch {
    return undefined;
  }
}

/**
 * ゲートウェイ一覧メイン画面
 * @returns JSX.Element
 */
const GWMain = () => {
  const loaderData = useLoaderData() as loaderReturn | undefined;
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {gwID, systemType, ouID} = useParams();

  const [siteInfo, setSiteInfo] =
    useState<apiIF.res.getCompositionsSitesBySiteID | null>(null); //サイト情報
  const [allGWInfo, setAllGWInfo] =
    useState<apiIF.res.getCompositionsGateway | null>(null); //ゲートウェイ情報
  const [displayGWInfo, setDisplayGWInfo] =
    useState<apiIF.res.getCompositionsGatewayByGatewayID | null>(null); //表示しているゲートウェイの情報
  const [defaultExpanded, setDefaultExpanded] = useState<string[]>(['0']); //ツリービューで初期に開く階層を指定（nodeIDで指定）

  useEffect(() => {
    if (loaderData === undefined) {
      return;
    }
    setSiteInfo(loaderData);
  }, [loaderData]);

  useEffect(() => {
    //ツリービューで初期に開く階層を決定
    if (gwID !== undefined && location.pathname.endsWith('communication')) {
      //通信系統番号画面
      setDefaultExpanded([gwID]);
    } else if (
      gwID !== undefined &&
      systemType !== undefined &&
      (location.pathname.endsWith('sl-ou-registration') ||
        location.pathname.endsWith('rt-registration') ||
        location.pathname.endsWith('modbus-ou-registration'))
    ) {
      //SL通信室外機一覧画面
      //熱源機一覧画面
      //Modbus通信室外機一覧画面
      setDefaultExpanded([gwID, `${gwID}${systemType}`]);
    } else if (
      gwID !== undefined &&
      systemType !== undefined &&
      ouID !== undefined &&
      (location.pathname.endsWith('sl-iu-registration') ||
        location.pathname.endsWith('rt-iu-registration') ||
        location.pathname.endsWith('modbus-iu-registration'))
    ) {
      //SL通信室内機一覧画面
      //RT通信室内機一覧画面
      //Modbus通信室内機一覧画面
      setDefaultExpanded([gwID, `${gwID}${systemType}`, ouID]);
    } else if (
      gwID !== undefined &&
      systemType !== undefined &&
      location.pathname.endsWith('sl-other-registration')
    ) {
      //その他空調機一覧
      //SL通信室内機一覧画面
      setDefaultExpanded([
        gwID,
        `${gwID}${systemType}`,
        `${gwID}${systemType}other`,
      ]);
    }

    //表示しているゲートウェイの情報を取得
    if (gwID !== undefined && allGWInfo !== null) {
      const temp = allGWInfo['gateway-informations'].find(
        gwInfo => gwInfo['gateway-id'] === gwID
      );
      if (
        temp !== undefined &&
        temp['gateway-name'] !== undefined &&
        temp['system'] !== undefined &&
        temp['sl-type'] !== undefined &&
        temp['modbus-tcp'] !== undefined &&
        temp['gateway-version'] !== undefined &&
        temp['gateway-update-version'] !== undefined &&
        temp['ip-address'] !== undefined &&
        temp['received-time'] !== undefined &&
        temp['composition-update-time'] !== undefined &&
        temp['composition-receipt-version'] !== undefined &&
        temp['communication-test-time'] !== undefined &&
        temp['register-number'] !== undefined &&
        temp['compositions'] !== undefined
      ) {
        setDisplayGWInfo({
          'gateway-id': temp['gateway-id'],
          'gateway-name': temp['gateway-name'],
          'system': temp['system'],
          'sl-type': temp['sl-type'],
          'modbus-tcp': temp['modbus-tcp'],
          'gateway-version': temp['gateway-version'],
          'gateway-update-version': temp['gateway-update-version'],
          'ip-address': temp['ip-address'],
          'received-time': temp['received-time'],
          'composition-update-time': temp['composition-update-time'],
          'composition-receipt-version': temp['composition-receipt-version'],
          'communication-test-time': temp['communication-test-time'],
          'register-number': temp['register-number'],
          'compositions': temp['compositions'],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, allGWInfo]);

  return (
    <Box className="gwSetting">
      <Grid container>
        {/** ツリービュー */}
        {/** lgより小さい画面では非表示 */}
        <Grid item lg={3} sx={{display: {xs: 'none', lg: 'block'}}}>
          <UnitSettingTreeView
            defaultExpanded={defaultExpanded}
            gwInfo={allGWInfo}
          />
        </Grid>
        <Grid item xs={12} lg={9}>
          <Container fixed>
            {/* パンくずリスト */}
            <RegistrationBreadcrumbs
              siteName={siteInfo === null ? undefined : siteInfo['site-name']}
              gwInfo={displayGWInfo === null ? undefined : displayGWInfo}
            />
            <Outlet
              context={{
                siteInfo: siteInfo,
                allGWInfo: allGWInfo,
                setAllGWInfo: setAllGWInfo,
              }}
            />
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};
const useOutlet = () => useOutletContext<OutletContextType>();

export {useOutlet};
export default GWMain;
